/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef, useTransition } from "react";
import { createPortal } from 'react-dom';
import "../styles/components/jobDetailsModal.scss";
import ResumeComponent from "./resumeTailoring";
import CoverLetter from "./CoverLetter"; // Import the new CoverLetter component
import axiosClient from "../axios-client";
import aiIcon from "../assets/ai-blue.svg"; // Path to AI blue icon
import JobDescriptionIcon from "../assets/job-description.svg"; // Path to job description icon
import { v4 as uuidv4 } from "uuid";
import formatDistance from "date-fns/formatDistance";
import { toast } from "react-hot-toast";
import { ResumeText } from './resumeTailoring';
import { addUtmParameters } from '../utils/utm';
import ApplicationConfirmModal from './ApplicationConfirmModal';
import { useApplicationModal } from '../contexts/ApplicationModalContext';
import { blobStorageService } from '../services/blobStorage.service';
import { jobDetailsService } from "../services/jobDetails.service";
import companyInfoService, { CompanyInfo } from '../services/companyInfo.service';
import axios from 'axios';
import { useRequireLogin } from '../hooks/useRequireLogin';
import { useUser } from '../contexts/UserContext';

export interface JobData {
  _id: string;
  title: string;
  original_title?: string;
  company: string;
  location: string;
  location_hub?: string;
  summary?: string;
  qualifications?: string[];
  responsibilities?: string[];
  environment?: string;
  workEnvironment?: string;
  work_environment?: string;
  skills?: string | string[];
  salary?: string;
  experience_level?: string;
  jobType?: string;
  tailored_flag?: string;
  days_to_live?: any;
  job_url: string;
  isProcessed?: boolean | string;
  isDemo?: boolean;
}

interface JobDetailsModalProps {
  job: JobData;
  isOpen: boolean;
  onClose: () => void;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({
  job: initialJob,
  isOpen,
  onClose,
}) => {
  const [localIsOpen, setLocalIsOpen] = useState(isOpen);
  
  // Sync with parent's isOpen prop
  useEffect(() => {
    setLocalIsOpen(isOpen);
  }, [isOpen]);

  const [selectedSection, setSelectedSection] = useState("job-details");
  const [resumeText, setResumeText] = useState<ResumeText | null>(null);
  const [resumeId, setResumeId] = useState<string>('');
  const [showCoverLetterTab, setShowCoverLetterTab] = useState(false);
  const [showResumeTab, setShowResumeTab] = useState(false); // New state to control Resume tab visibility
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0); // For logo fallback
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<JobData>(initialJob);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Enhanced transition state management
  const [contentState, setContentState] = useState<'skeleton' | 'transitioning' | 'content'>('skeleton');
  const [isPending, startTransition] = useTransition();

  // Add new state variables to track tailoring status
  const [hasResumeBeenTailored, setHasResumeBeenTailored] = useState(false);
  const [hasCoverLetterBeenTailored, setHasCoverLetterBeenTailored] = useState(false);

  // Session-level state (persists during modal session, clears on close)
  const [sessionResumeContent, setSessionResumeContent] = useState<{
    suggestions: any[];
    score: number | null;
  } | null>(null);
  
  const [sessionCoverLetter, setSessionCoverLetter] = useState<string | null>(null);
  


  const [showApplicationConfirm, setShowApplicationConfirm] = useState(false);
  const [hasLeftForExternalSite, setHasLeftForExternalSite] = useState(false);

  const { setModalData } = useApplicationModal();

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoVariant, setLogoVariant] = useState<'horizontal' | 'square' | 'vertical' | null>(null);

  const [showAllLocations, setShowAllLocations] = useState(false);

  // Add new state variables for job details processing
  const [isProcessingJobDetails, setIsProcessingJobDetails] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Add refs for proper cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const backgroundProcessingRef = useRef<Promise<any> | null>(null);

  // Add closing ref to prevent infinite loops - more reliable than state
  const isClosingRef = useRef(false);

  const requireLogin = useRequireLogin();
  const { isAuthenticated } = useUser();

  // Extract job type from job data
  const extractJobType = (job: JobData): string => {
    // Method 1: direct extraction from standard fields
    if (job.jobType) {
      return job.jobType;
    } 
    // Method 2: force a fallback value
    return "Full Time"; // Default fallback
  };

  // Add helper function to normalize job type strings
  const normalizeJobType = (jobType: string): string => {
    if (!jobType) return '';
    
    // Remove both prefix and any surrounding quotes
    return jobType
      .replace(/^Job Type:\s*/i, '')
      .replace(/^['"]|['"]$/g, '') // Remove surrounding quotes
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Function to map job types to corresponding class names
  const getJobTypeClass = (jobType: string): string => {
    if (!jobType) return '';
    
    if (jobType === 'DEMO') return 'demo';
    
    const normalizedType = normalizeJobType(jobType);
    
    switch (normalizedType) {
      case 'full time':
      case 'fulltime':
      case 'full-time':
        return 'full-time';
      case 'part time':
      case 'parttime':
      case 'part-time':
        return 'part-time';
      case 'internship':
      case 'intern':
        return 'internship';
      case 'contract':
      case 'contractor':
      case 'contractual':
        return 'contract';
      default:
        return normalizedType.replace(/\s+/g, '-');
    }
  };

  const formatJobType = (jobType: string): string => {
    if (!jobType) return '';
    
    if (jobType === 'DEMO') return 'DEMO';
    
    const normalizedType = normalizeJobType(jobType);
    
    switch (normalizedType) {
      case 'full time':
      case 'fulltime':
      case 'full-time':
        return 'FULL-TIME';
      case 'part time':
      case 'parttime':
      case 'part-time':
        return 'PART-TIME';
      case 'internship':
      case 'intern':
        return 'INTERNSHIP';
      case 'contract':
      case 'contractor':
      case 'contractual':
        return 'CONTRACT';
      default:
        return jobType.toUpperCase().replace(/[-_]/g, ' ');
    }
  };

  // Function to format job type with proper capitalization for display in the details sidebar
  const normalizeJobTypeForDisplay = (jobType: string): string => {
    if (!jobType) return 'Not specified';
    
    const normalizedType = normalizeJobType(jobType);
    
    switch (normalizedType) {
      case 'full time':
      case 'fulltime':
      case 'full-time':
        return 'Full-Time';
      case 'part time':
      case 'parttime':
      case 'part-time':
        return 'Part-Time';
      case 'internship':
      case 'intern':
        return 'Internship';
      case 'contract':
      case 'contractor':
      case 'contractual':
        return 'Contract';
      default:
        // Capitalize first letter of each word
        return normalizedType
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    }
  };

  // Immediate cleanup function - kills everything instantly
  const immediateCleanup = () => {
    // 1. Abort all ongoing requests IMMEDIATELY - but only if we're actually closing
    if (abortControllerRef.current && isClosingRef.current) {
      abortControllerRef.current.abort();
    }
    // Always reset the ref to null for clean state
    abortControllerRef.current = null;

    // 2. Clear all timers immediately - professional cleanup
    timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutRefs.current = [];

    // 3. Reset all states immediately - complete memory cleanup
    setIsLoading(false);
    setIsProcessingJobDetails(false);
    setContentState('skeleton');
    setError(null);
    setProcessingError(null);
    setHasAnimated(false);
    setHasResumeBeenTailored(false);
    setHasCoverLetterBeenTailored(false);
    setShowCoverLetterTab(false);
    setShowResumeTab(false);
    setSelectedSection("job-details");
    setResumeText(null);
    setSessionResumeContent(null);
    setSessionCoverLetter(null);
    setResumeId('');
    setLogoUrl(null);
    setLogoVariant('square');
    setCompanyInfo(null);
    
    // Reset closing ref for clean shutdown
    isClosingRef.current = false;

    // 4. Restore DOM immediately - clean state
    document.body.style.overflow = '';
    document.body.classList.remove('skeleton-transitioning');

    // 5. Clean up global references - professional cleanup
    delete (window as any).retryJobProcessing;

    // 6. Clear localStorage for this job session - memory cleanup
    try {
      localStorage.removeItem(`coverLetter_${initialJob._id}`);
      localStorage.removeItem(`resumeContent_${initialJob._id}`);
      localStorage.removeItem(`resumeScore_${initialJob._id}`);
      localStorage.removeItem(`tailoredResume_${initialJob._id}`);
      localStorage.removeItem(`resumeId_${initialJob._id}`);
    } catch (error) {
      // Silently handle localStorage errors
    }
  };

  // Background AI processing function - ONLY for actual job processing, not errors
  const continueBackgroundProcessing = async (jobId: string, processingPromise: Promise<any>) => {
    try {
      const result = await processingPromise;
      
      // Only store successful results - don't try to store errors to non-existent endpoints
      if (result) {
        try {
          await axiosClient.post(`/jobs/${jobId}/store-processed-data`, {
            processedData: result,
            timestamp: new Date().toISOString()
          });
        } catch (storeError) {
          // Silently handle storage errors
        }
      }
    } catch (error) {
      // Silently handle processing errors
    }
  };

  // Track when modal opens for analytics/monitoring
  useEffect(() => {
    if (localIsOpen) {
      // Modal opened - could add analytics here
    }
  }, [localIsOpen, job]);

  useEffect(() => {
    const loadLogo = async () => {
      if (job.company && localIsOpen) {
        try {
          // Ensure we have an abort controller
          if (!abortControllerRef.current) {
            abortControllerRef.current = new AbortController();
          }
          
          const logo = await blobStorageService.getBestLogoVariant(job.company);
          if (logo && !abortControllerRef.current?.signal.aborted) {
            setLogoUrl(logo.url);
            setLogoVariant(logo.variant);
          }
        } catch (error) {
          if (!abortControllerRef.current?.signal.aborted) {
            console.error('Error loading logo:', error);
          }
        }
      }
    };
    if (localIsOpen) {
      loadLogo();
    }
  }, [job.company, localIsOpen]);

  useEffect(() => {
    if (localIsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [localIsOpen]);

  // Separate effect for abort controller - runs before other async operations
  useEffect(() => {
    if (localIsOpen) {
      // Create new abort controller for this modal session
      abortControllerRef.current = new AbortController();
    }
  }, [localIsOpen]);

  useEffect(() => {
    if (localIsOpen && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [localIsOpen]);

  // Single cleanup effect - handles all cleanup in one place
  useEffect(() => {
    return () => {
      immediateCleanup();
    };
  }, []);

  // Add cleanup for any leftover transitions
  useEffect(() => {
    return () => {
      // Clean up any transitions when component unmounts
      document.body.classList.remove('skeleton-transitioning');
      setContentState('skeleton'); // Reset state for next open
      
      // Ensure no flickering on next open
      const skeletonEl = document.querySelector('.skeleton-loading');
      if (skeletonEl) {
        skeletonEl.classList.remove('fade-out');
        const headings = skeletonEl.querySelectorAll('.section-heading');
        headings.forEach(heading => {
          heading.classList.remove('persistent');
        });
      }
    };
  }, []);

  // PROFESSIONAL MODAL CLOSE - Complete shutdown with selective background processing
  const handleClose = (skipOnClose = false) => {
    // Prevent multiple close calls - professional guard using ref
    if (isClosingRef.current) {
      return;
    }
    
    isClosingRef.current = true;
    
    // Step 1: Immediate and complete cleanup of all modal resources
    immediateCleanup();
    
    // Step 2: ONLY continue background processing if there's ACTUAL AI job processing happening
    // (not for resume fetching, company info, or other modal-specific tasks)
    if (backgroundProcessingRef.current && isProcessingJobDetails) {
      continueBackgroundProcessing(initialJob._id, backgroundProcessingRef.current);
      // Clear the ref so it doesn't run again
      backgroundProcessingRef.current = null;
    }
    
    // Step 3: Close modal gracefully
    setLocalIsOpen(false);
    
    // CRITICAL: Only call onClose for initial close (not for event-triggered closes)
    // This prevents the circular loop: handleClose -> onClose -> closeJobModal -> event -> handleForceClose -> handleClose
    if (!skipOnClose) {
      onClose();
    }
  };

  // Optimized fetchResume effect - prevents multiple calls
  useEffect(() => {
    if (localIsOpen && !isClosingRef.current) {
      fetchResume();
    }
  }, [localIsOpen]); // Removed job._id dependency to prevent multiple calls

  // Add effect to persist cover letter in localStorage




  // Add effect to handle window focus
  useEffect(() => {
    const handleWindowFocus = () => {
      if (hasLeftForExternalSite && !abortControllerRef.current?.signal.aborted) {
        setModalData({ 
          showModal: true, 
          jobTitle: job.title, 
          companyName: job.company,
          jobId: job._id,
          getResumeContent: () => resumeText,
        });
        setHasLeftForExternalSite(false);
      }
    };

    if (localIsOpen) {
      window.addEventListener('focus', handleWindowFocus);
      return () => window.removeEventListener('focus', handleWindowFocus);
    }
  }, [hasLeftForExternalSite, job.title, job.company, job._id, setModalData, resumeText, localIsOpen]);

  // Add effect to listen for custom job-modal-close event - SIMPLIFIED
  useEffect(() => {
    const handleForceClose = () => {
      // Prevent infinite loops using ref - much more reliable
      if (isClosingRef.current) {
        return;
      }
      
      // CRITICAL: Pass skipOnClose=true to prevent circular call back to context
      handleClose(true);
    };
    
    if (localIsOpen) {
      window.addEventListener('job-modal-close', handleForceClose);
      return () => {
        window.removeEventListener('job-modal-close', handleForceClose);
      };
    }
  }, [localIsOpen]); // Removed isClosing dependency to prevent recreation

  // Add a new effect to reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset to skeleton state immediately when modal opens
      setContentState('skeleton');
      setIsLoading(true);
      // Reset closing ref when modal opens
      isClosingRef.current = false;
      // Force skeleton to be visible immediately
      document.body.classList.add('skeleton-transitioning');
    }
  }, [isOpen]);

  // Modified fetchJobDetails with proper abort handling and background processing
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (localIsOpen && initialJob._id) {
        // Ensure we have an abort controller
        if (!abortControllerRef.current) {
          abortControllerRef.current = new AbortController();
        }
        // Skip all processing for demo jobs
        if (initialJob._id.startsWith('demo-') || initialJob.jobType === 'DEMO') {
          if (!abortControllerRef.current.signal.aborted) {
            setJob(initialJob);
            setIsLoading(false);
            setContentState('content');
            setError(null);
            setProcessingError(null);
          }
          return;
        }

        try {
          // Check if aborted before starting
          if (abortControllerRef.current.signal.aborted) return;
          
          // Set proper loading states immediately
          setIsLoading(true);
          setContentState('skeleton');
          setError(null);
          setProcessingError(null);
          
          // Add a class to the body for proper skeleton animation
          document.body.classList.add('skeleton-transitioning');
          
          // Get basic job info first and ensure we capture any job_url that might be present
          const basicJob = { 
            ...initialJob,
            job_url: initialJob.job_url || '' // Ensure job_url is never undefined
          };
          
          // Update the state immediately with whatever job_url we have from initial job
          if (!abortControllerRef.current.signal.aborted) {
            setJob(basicJob);
          }
          
          // Start company info fetch in parallel
          const companyInfoPromise = companyInfoService.getCompanyInfo(initialJob.company);
          
          // Properly handle isProcessed regardless of type (convert string "True"/"False" to boolean)
          const isProcessed = 
            typeof basicJob.isProcessed === 'boolean' ? basicJob.isProcessed :
            typeof basicJob.isProcessed === 'string' ? 
              basicJob.isProcessed.toLowerCase() === 'true' : false;

          // If already processed, just use the existing data
          if (isProcessed) {
            // INSTANT loading for already processed jobs - no fake delays!
            if (!abortControllerRef.current?.signal.aborted) {
              // Get company info in parallel but don't wait for it
              companyInfoService.getCompanyInfo(initialJob.company)
                .then(companyData => {
                  if (companyData && !abortControllerRef.current?.signal.aborted) {
                    setCompanyInfo(companyData);
                  }
                })
                .catch(() => {
                  // Silently handle company info error
                });

              // Immediately show content - no delays!
              setContentState('content');
              setIsLoading(false);
              document.body.classList.remove('skeleton-transitioning');
            }
            
            return;
          }
          
          // If not processed, process it with background handling
          try {
            if (abortControllerRef.current.signal.aborted) return;
            
            setIsProcessingJobDetails(true);
            
            // Create the AI processing promise
            const aiProcessingPromise = Promise.all([
              jobDetailsService.getJobDetails(initialJob._id),
              companyInfoPromise
            ]);
            
            // Store reference for background processing
            backgroundProcessingRef.current = aiProcessingPromise;
            
            // Fetch job details and company info in parallel
            const [processedData, companyData] = await aiProcessingPromise;
            
            // Check if still mounted before updating state
            if (abortControllerRef.current?.signal.aborted) {
              // Modal was closed, but AI processing completed - store in background
              continueBackgroundProcessing(initialJob._id, Promise.resolve([processedData, companyData]));
              return;
            }
            
            // Handle company info
            if (companyData) {
              setCompanyInfo(companyData);
            }
            
            // Create an updated job object that prioritizes job_url from the API response
            const updatedJob = {
              ...basicJob,
              responsibilities: processedData.responsibilities || [],
              qualifications: processedData.qualifications || [],
              salary: processedData.salary || '',
              jobType: processedData.jobType || '',
              workEnvironment: processedData.workEnvironment || '',
              skills: processedData.skills || [],
              summary: processedData.summary || '',
              isProcessed: true,
            };
            
            // Handle job_url - CRITICAL: if processed data has a job_url, use it
            if (processedData.job_url && processedData.job_url.trim() !== '') {
              updatedJob.job_url = processedData.job_url;
            }
            
            // Update job data immediately
            setJob(updatedJob);
            
            // INSTANT transition after AI processing completes - no fake delays!
            if (!abortControllerRef.current?.signal.aborted) {
              setContentState('content');
              setIsProcessingJobDetails(false);
              setIsLoading(false);
              document.body.classList.remove('skeleton-transitioning');
            }

          } catch (processingError) {
            // Check if error is due to abort
            if (abortControllerRef.current?.signal.aborted) return;
            
            // Don't show errors if the request was just aborted/canceled
            const errorMessage = processingError instanceof Error ? processingError.message : String(processingError);
            if (!errorMessage.toLowerCase().includes('abort') && !errorMessage.toLowerCase().includes('cancel')) {
              setProcessingError('Failed to process job details. Please try again.');
            }
            
            setIsLoading(false);
          }
        } catch (error) {
          if (!abortControllerRef.current?.signal.aborted) {
            // Don't show errors if the request was just aborted/canceled
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (!errorMessage.toLowerCase().includes('abort') && !errorMessage.toLowerCase().includes('cancel')) {
              setError('Failed to fetch job details. Please check your connection and try again.');
            }
            setIsLoading(false);
            setContentState('content'); // Ensure we exit the loading state
          }
        }
      }
    };

    // Call the function
    fetchJobDetails();
  }, [
    localIsOpen, 
    initialJob._id, 
    initialJob.company, 
    initialJob.job_url, 
    initialJob.isProcessed,
    initialJob
  ]);

  const fetchResume = async () => {
    try {
      if (!user?._id || !job?._id) {
        throw new Error('Missing user or job data');
      }

      // Wait a bit for abort controller to be properly set up
      await new Promise(resolve => setTimeout(resolve, 10));
      
      if (!abortControllerRef.current) {
        throw new Error('AbortController not available');
      }

      // Check if aborted before starting
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      setIsLoading(true); // Add loading state

      // Try to get existing resume with abort signal
      const resume = await axiosClient.get(`builder/get-resume/${user._id}/${job._id}`, {
        signal: abortControllerRef.current.signal
      });
      
      // Check if aborted after API call
      if (abortControllerRef.current.signal.aborted) {
        return;
      }
      
      if (resume?.data) {
        setResumeId(resume.data._id);
        setResumeText(transformData(resume.data));
      } else {
        // Fallback to user data if no resume exists
        const response = await axiosClient.get(`users/${user._id}`, {
          signal: abortControllerRef.current.signal
        });
        
        // Check if aborted after fallback API call
        if (abortControllerRef.current.signal.aborted) {
          return;
        }
        
        if (response?.data) {
          setResumeText(transformData(response.data));
        } else {
          throw new Error('No user data found');
        }
      }
    } catch (error) {
      // Only log errors if not canceled and controller exists
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (!errorMessage.toLowerCase().includes('abort') && !errorMessage.toLowerCase().includes('cancel')) {
          setError(error instanceof Error ? error.message : 'Failed to fetch resume');
        }
      }
    } finally {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleTailorButtonClick = () => {
    if (abortControllerRef.current?.signal.aborted) return;
    
    // Gate this feature behind login
    if (!requireLogin()) return;

    setShowResumeTab(true); // Show the resume tab
    setShowCoverLetterTab(true);
    setSelectedSection("resume");
    // Cache is preserved - no clearing here, only when modal closes
  };

  const handleNavigateToCoverLetter = () => {
    if (abortControllerRef.current?.signal.aborted) return;
    
    setSelectedSection("cover-letter");
  };

  // Move the conditional return before any useEffect calls that follow it
  if (!localIsOpen) return null;

  const tabsConfig = [
    {
      id: "job-details",
      label: "Job Details",
      icon: (
        <img
          src={JobDescriptionIcon}
          alt="Job Description Icon"
          width="16"
          height="16"
          className="tab-icon"
        />
      ),
    },
    ...(showResumeTab
      ? [
          {
            id: "resume",
            label: "Resume",
            icon: (
              <img
                src={aiIcon}
                alt="Resume Icon"
                width="16"
                height="16"
                className="tab-icon"
              />
            ),
          },
        ]
      : []),
    ...(showCoverLetterTab
      ? [
          {
            id: "cover-letter",
            label: "Cover Letter",
            icon: (
              <img
                src={aiIcon}
                alt="Cover Letter Icon"
                width="16"
                height="16"
                className="tab-icon"
              />
            ),
          },
        ]
      : []),
  ];

  // Simplified getPostedDate function
  const getPostedDate = (daysToLive: any) => {
    // Handle MongoDB $numberInt format or regular number
    const days = daysToLive?.$numberInt 
      ? parseInt(daysToLive.$numberInt) 
      : typeof daysToLive === 'number' 
        ? daysToLive 
        : null;

    if (days === null) {
      return "Recently Posted";
    }

    const totalDays = 15;
    const daysAgo = totalDays - days;

    switch (daysAgo) {
      case 0:
        return "Today";
      case 1:
        return "Yesterday";
      default:
        return `${daysAgo} days ago`;
    }
  };

  const getStatusClass = (daysToLive: any): string => {
    const days = daysToLive?.$numberInt 
      ? parseInt(daysToLive.$numberInt) 
      : typeof daysToLive === 'number' 
        ? daysToLive 
        : 0;

    if (days >= 10) return 'recent';
    if (days >= 5) return 'moderate';
    return 'old';
  };

  // Function to get all unique locations
  const getLocations = (location: string): string[] => {
    if (!location) return [];
    const locationsArray = location.split(';').map(loc => loc.trim());
    return Array.from(new Set(locationsArray));
  };

  // Function to get first location for header
  const getFirstLocation = (location: string): string => {
    if (!location) return '';
    return location.split(';')[0].trim();
  };

  const renderLocations = (locations: string[]) => {
    // Remove the first location since it's shown in the header
    const additionalLocations = locations.slice(1);
    if (additionalLocations.length === 0) return null;

    const visibleLocations = showAllLocations 
      ? additionalLocations 
      : additionalLocations.slice(0, 3);
    const hasMore = additionalLocations.length > 3;

    return (
      <div className="locations-section">
        <h4>Additional Locations</h4>
        <div className={`locations-list ${showAllLocations ? 'expanded' : ''}`}>
          {visibleLocations.map((location, index) => (
            <p key={index}>{location}</p>
          ))}
          {hasMore && !showAllLocations && (
            <p className="ellipsis">...</p>
          )}
          {hasMore && (
            <button 
              className="show-more-button"
              onClick={() => setShowAllLocations(!showAllLocations)}
            >
              {showAllLocations ? 'Show Less' : `Show ${additionalLocations.length - 3} More`}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Determine if we're in any loading state
    const isDataLoading = isLoading || isProcessingJobDetails;
    
    if (error) {
      return (
        <div className="error-container">
          <p>{error}</p>
        </div>
      );
    }

    if (processingError) {
      return (
        <div className="error-container animate-subtle-pulse" onClick={() => (window as any).retryJobProcessing?.()}>
          <p>{processingError}</p>
        </div>
      );
    }

    const jobToDisplay = job;

    switch (selectedSection) {
      case "job-details":
        return (
          <div className={`content-transition-container ${contentState === 'transitioning' ? 'transitioning' : ''}`}>
            {/* Always render the real content when transitioning or fully loaded */}
            {(contentState === 'transitioning' || contentState === 'content') && (
              <div className={`real-content ${contentState === 'content' ? 'fade-in' : ''}`}>
                <div className="flex flex-col w-full">
                  {/* Job description section */}
                  <div className="job-description">
                    <div className="content-container">
                      <div className="left-column">
                        {/* About the Job */}
                        <div className="detail-section">
                          <h3 className="section-heading">About the Job:</h3>
                          <div className="section-content" data-replacing-skeleton="true">
                            {jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? (
                              <p>
                                QuickCruit is seeking a talented {jobToDisplay.title} to join our growing team. This role offers an opportunity to work on innovative projects while developing your professional skills. The ideal candidate is passionate about creating impactful solutions and thrives in a collaborative environment.
                              </p>
                            ) : (
                              <p>{jobToDisplay.summary || "No description provided."}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Requirements/Qualifications */}
                        <div className="detail-section">
                          <h3 className="section-heading">Requirements:</h3>
                          <div className="section-content" data-replacing-skeleton="true">
                            {jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? (
                              <ul className="requirements-list">
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="requirement-text">Bachelor's degree in Marketing, Business, or related field</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="requirement-text">3+ years of experience in product marketing or similar role</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="requirement-text">Strong analytical and strategic thinking abilities</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="requirement-text">Excellent written and verbal communication skills</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="requirement-text">Experience with market research and competitive analysis</span>
                                </li>
                              </ul>
                            ) : jobToDisplay.qualifications && jobToDisplay.qualifications.length > 0 ? (
                              <ul className="requirements-list">
                                {jobToDisplay.qualifications.map((req, index) => (
                                  <li key={index}>
                                    <span className="bullet-point">•</span>
                                    <span className="requirement-text">{req}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No requirements specified.</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Responsibilities */}
                        <div className="detail-section">
                          <h3 className="section-heading">Responsibilities:</h3>
                          <div className="section-content" data-replacing-skeleton="true">
                            {jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? (
                              <ul className="responsibilities-list">
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="responsibility-text">Develop and execute product marketing strategies</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="responsibility-text">Create compelling product messaging and positioning</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="responsibility-text">Conduct market research and analyze competitive landscape</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="responsibility-text">Collaborate with cross-functional teams to drive product success</span>
                                </li>
                                <li>
                                  <span className="bullet-point">•</span>
                                  <span className="responsibility-text">Track and report on key performance metrics</span>
                                </li>
                              </ul>
                            ) : jobToDisplay.responsibilities && jobToDisplay.responsibilities.length > 0 ? (
                              <ul className="responsibilities-list">
                                {jobToDisplay.responsibilities.map((resp, index) => (
                                  <li key={index}>
                                    <span className="bullet-point">•</span>
                                    <span className="responsibility-text">{resp}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No responsibilities specified.</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Skills */}
                        <div className="detail-section">
                          <h3 className="section-heading">Skills:</h3>
                          <div className="section-content" data-replacing-skeleton="true">
                            <div className="skills">
                              {jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? (
                                <>
                                  <span className="skill-badge">Product Marketing</span>
                                  <span className="skill-badge">Market Research</span>
                                  <span className="skill-badge">Strategic Planning</span>
                                  <span className="skill-badge">Data Analysis</span>
                                  <span className="skill-badge">Project Management</span>
                                  <span className="skill-badge">Communication</span>
                                </>
                              ) : jobToDisplay.skills ? (
                                Array.isArray(jobToDisplay.skills) ? (
                                  jobToDisplay.skills.map((skill, index) => (
                                    <span key={index} className="skill-badge">
                                      {skill.replace(/^•\s*/, "").trim()}
                                    </span>
                                  ))
                                ) : (
                                  jobToDisplay.skills.split(",").map((skill, index) => (
                                    <span key={index} className="skill-badge">
                                      {skill.trim()}
                                    </span>
                                  ))
                                )
                              ) : (
                                <p>No skills specified.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="right-column">
                        <div className="job-info-box">
                          <h4 className="section-heading">
                            Posted
                            <span className={`status-dot ${getStatusClass(jobToDisplay.days_to_live)}`} />
                          </h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <p>{getPostedDate(jobToDisplay.days_to_live)}</p>
                          </div>
                          
                          {/* Salary Information */}
                          <h4 className="section-heading">Salary Range</h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <p>{jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? "$180,000—$250,000 USD" : (jobToDisplay.salary || "N/A")}</p>
                          </div>
                          
                          {/* Experience Level */}
                          <h4 className="section-heading">Experience Level</h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <p>{jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? "Mid Career (3+ years)" : (jobToDisplay.experience_level || "Not specified")}</p>
                          </div>
                          
                          {/* Job Type - renamed from Employment Type */}
                          <h4 className="section-heading">Job Type</h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <p>{normalizeJobTypeForDisplay(extractJobType(jobToDisplay))}</p>
                          </div>
                          
                          {/* Work Environment */}
                          <h4 className="section-heading">Work Environment</h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <p>{jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo ? "Hybrid" : (jobToDisplay.workEnvironment || jobToDisplay.environment || jobToDisplay.work_environment || "Not specified")}</p>
                          </div>
                          
                          {/* Locations */}
                          {renderLocations(getLocations(jobToDisplay.location))}
                          
                          {/* About Company */}
                          <h4 className="section-heading about-company">About the Company</h4>
                          <div className="section-content" data-replacing-skeleton="true">
                            <div className="company-description-container">
                              <p className="about-company-text">
                                {jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo 
                                  ? "QuickCruit is a leading technology company revolutionizing the job search and application process. We're dedicated to helping job seekers find and land their dream roles through innovative solutions and cutting-edge technology."
                                  : (companyInfo && companyInfo.description 
                                      ? companyInfo.description 
                                      : `${jobToDisplay.company} is a leader in its industry providing innovative solutions and services.`)}
                              </p>
                              {companyInfo && companyInfo.website_url && !(jobToDisplay.jobType === 'DEMO' || jobToDisplay.isDemo) && (
                                <a 
                                  href={addUtmParameters(companyInfo.website_url)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="external-link"
                                >
                                  Learn More <svg className="external-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 3V9H19V6.414L11.207 14.207L9.793 12.793L17.586 5H15V3H21Z" fill="currentColor"/>
                                    <path d="M19 19H5V5H12V3H5C3.897 3 3 3.897 3 5V19C3 20.103 3.897 21 5 21H19C20.103 21 21 20.103 21 19V12H19V19Z" fill="currentColor"/>
                                  </svg>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show skeleton when loading or transitioning */}
            {(contentState === 'skeleton' || contentState === 'transitioning') && (
              <div className={`skeleton-content ${contentState === 'transitioning' ? 'fade-out' : ''}`}>
                <div className="flex flex-col w-full job-modal-skeleton">
                  {/* Job description section with skeleton loading */}
                  <div className="job-description">
                    <div className="content-container">
                      <div className="left-column">
                        {/* About the Job */}
                        <div className="detail-section">
                          <h3 className="section-heading">About the Job:</h3>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-line"></div>
                            <div className="job-modal-skeleton-line"></div>
                            <div className="job-modal-skeleton-line"></div>
                          </div>
                        </div>
                        
                        {/* Requirements/Qualifications */}
                        <div className="detail-section">
                          <h3 className="section-heading">Requirements:</h3>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-list">
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Responsibilities */}
                        <div className="detail-section">
                          <h3 className="section-heading">Responsibilities:</h3>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-list">
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                              <div className="job-modal-skeleton-item">
                                <span className="bullet-point">•</span>
                                <div className="job-modal-skeleton-line"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Skills */}
                        <div className="detail-section">
                          <h3 className="section-heading">Skills:</h3>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-skills">
                              <div className="job-modal-skeleton-skill"></div>
                              <div className="job-modal-skeleton-skill"></div>
                              <div className="job-modal-skeleton-skill"></div>
                              <div className="job-modal-skeleton-skill"></div>
                              <div className="job-modal-skeleton-skill"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="right-column">
                        <div className="job-info-box">
                          <h4 className="section-heading">Posted <span className={`status-dot ${getStatusClass(job.days_to_live || initialJob.days_to_live)}`}></span></h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                          
                          <h4 className="section-heading">Salary Range</h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                          
                          <h4 className="section-heading">Experience Level</h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                          
                          <h4 className="section-heading">Job Type</h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                          
                          <h4 className="section-heading">Work Environment</h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                          
                          <h4 className="section-heading about-company">About the Company</h4>
                          <div className="job-modal-skeleton-content">
                            <div className="job-modal-skeleton-data"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case "resume":
        return resumeText && (
          <ResumeComponent
            jobDescription={job}
            resumeText={resumeText}
            handleTailorButtonClick={handleTailorButtonClick}
            resumeId={resumeId}
            jobId={jobToDisplay._id}
            onNavigateToCoverLetter={handleNavigateToCoverLetter}
            hasBeenTailored={hasResumeBeenTailored}
            setHasBeenTailored={setHasResumeBeenTailored}
            cachedContent={sessionResumeContent}
            setCachedContent={setSessionResumeContent}
          />
        );
      case "cover-letter":
        return resumeText && (
          <CoverLetter 
            job={job} 
            resumeText={resumeText} 
            hasBeenTailored={hasCoverLetterBeenTailored}
            setHasBeenTailored={setHasCoverLetterBeenTailored}
            sessionContent={sessionCoverLetter}
            setSessionContent={setSessionCoverLetter}
          />
        );
      default:
        return null;
    }
  };

  // Modify tab switching to preserve tailoring state
  const handleTabChange = (tabId: string) => {
    setSelectedSection(tabId);
  };

  const handleExternalApply = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Get the application URL, ensuring it's not an empty string
    const applicationUrl = job.job_url && job.job_url.trim() !== '' ? job.job_url : null;
    
    if (applicationUrl) {
      // If the user is not logged in, allow direct navigation without resume tracking
      if (!isAuthenticated) {
        window.open(addUtmParameters(applicationUrl), '_blank', 'noopener,noreferrer');
        return;
      }

      // For authenticated users, ensure resume content exists before proceeding
      if (!resumeText) {
        console.error('No resume content available');
        toast.error('Resume content is required');
        return;
      }

      // Pass the current resume to ApplicationConfirmModal via context
      setModalData({ 
        showModal: true, 
        jobTitle: job.title, 
        companyName: job.company,
        jobId: job._id,
        getResumeContent: () => resumeText,
      });

      setHasLeftForExternalSite(true);
      window.open(addUtmParameters(applicationUrl), '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Application link not available for this job posting');
      
      // If we have the update job URL function, offer to update
      if (job._id) {
        const shouldUpdate = window.confirm('Would you like to provide an application URL for this job?');
        if (shouldUpdate) {
          handleUpdateJobUrl();
        }
      }
    }
  };

  const handleApplicationConfirm = (didApply: boolean) => {
    if (didApply) {
      // Here we'll later add the logic to track the application
    }
    setShowApplicationConfirm(false);
  };

  // Add a function to update job URL if missing
  const handleUpdateJobUrl = async () => {
    if (abortControllerRef.current?.signal.aborted) return;
    
    const newUrl = prompt("Enter the correct job application URL:", "");
    if (!newUrl || abortControllerRef.current?.signal.aborted) return;
    
    try {
      const response = await axiosClient.post(`/jobs/${job._id}/update-url`, {
        job_url: newUrl
      }, {
        signal: abortControllerRef.current?.signal
      });
      
      if (response.data && !abortControllerRef.current?.signal.aborted) {
        // Update the job object locally with the new URL
        setJob(prev => ({
          ...prev,
          job_url: newUrl
        }));
        
        toast.success('Job URL updated successfully');
      }
    } catch (error) {
      if (!abortControllerRef.current?.signal.aborted) {
        console.error('Error updating job URL:', error);
        toast.error('Failed to update job URL');
      }
    }
  };

  // Add this function to prevent outside clicks from closing the modal
  const handleModalClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent clicks from reaching the backdrop
    e.stopPropagation();
  };

  // Add this function to handle backdrop clicks (outside modal)
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only if clicked directly on backdrop, not when event bubbles
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      // Do not call onClose here - modal should stay open
    }
  };

  const user = JSON.parse(String(localStorage.getItem("user")));

  const transformData = (data: any): ResumeText => {
    const transformDescriptions = (items: any[]) => items.map(item => ({
      ...item,
      description: item.description.map((desc: any) =>
        typeof desc === "string" ? { id: uuidv4(), text: desc } : desc
      ),
    }));

    return {
      ...data,
      education: data.education.map((edu: any) => ({
        degree: edu.degree || '',
        field: edu.field || '',
        university: edu.university || '',
        graduationDate: edu.graduationDate || '',
        gpa: edu.gpa || ''
      })),
      experience: transformDescriptions(data.experience),
      projects: transformDescriptions(data.projects),
    };
  };

  return createPortal(
    <div 
      className={`modal-backdrop ${localIsOpen ? "show" : ""}`}
      onClick={handleBackdropClick} // Add handler to prevent closing on outside click
    >
      <div 
        className={`modal-content ${localIsOpen ? "show" : ""}`}
        onClick={handleModalClick} // Keep this to prevent bubbling
      >
        <div className="sticky-container">
          <div className="modal-header">
            <div className="tabs">
              {tabsConfig.map((tab) => (
                <div
                  key={tab.id}
                  className={`tab ${selectedSection === tab.id ? "selected" : ""}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span>{tab.label}</span>
                </div>
              ))}
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                handleClose(); // Call our handleClose function to close the modal
              }} 
              className="close-button" 
              aria-label="close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.2253 4.81108C5.83477 4.42056 5.20161 4.42056 4.81108 4.81108C4.42056 5.20161 4.42056 5.83477 4.81108 6.2253L10.5858 12L4.81114 17.7747C4.42062 18.1652 4.42062 18.7984 4.81114 19.1889C5.20167 19.5794 5.83483 19.5794 6.22535 19.1889L12 13.4142L17.7747 19.1889C18.1652 19.5794 18.7984 19.5794 19.1889 19.1889C19.5794 18.7984 19.5794 18.1652 19.1889 17.7747L13.4142 12L19.189 6.2253C19.5795 5.83477 19.5795 5.20161 19.189 4.81108C18.7985 4.42056 18.1653 4.42056 17.7748 4.81108L12 10.5858L6.2253 4.81108Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div className={`header-section ${hasAnimated ? 'animate' : ''} ${selectedSection === "job-details" ? 'visible' : ''}`}>
            <div className="job-title-section">
              <div className="top-row">
                <div className={`company-logo-wrapper ${logoVariant === 'horizontal' ? 'horizontal-logo-container' : ''}`}>
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${job.company} logo`}
                      className={`company-logo ${logoVariant === 'horizontal' ? 'horizontal-logo' : ''}`}
                    />
                  ) : (
                    <div className="logo-placeholder" />
                  )}
                </div>
                {/* Always show job type tag with extracted value */}
                <span
                  className={`job-type-tag ${getJobTypeClass(extractJobType(job))}`}
                >
                  {formatJobType(extractJobType(job))}
                </span>
              </div>
              <div className="bottom-row">
                <div className="job-title">
                  <h2>{job.title}</h2>
                  <p className="company-location">
                    {job.company} • {getFirstLocation(job.location)}
                  </p>
                </div>
              </div>
            </div>
            <div className="button-group">
              <a
                href="#"
                onClick={handleExternalApply}
                className="external-link"
              >
                Apply Externally <svg className="external-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 3V9H19V6.414L11.207 14.207L9.793 12.793L17.586 5H15V3H21Z" fill="currentColor"/>
                      <path d="M19 19H5V5H12V3H5C3.897 3 3 3.897 3 5V19C3 20.103 3.897 21 5 21H19C20.103 21 21 20.103 21 19V12H19V19Z" fill="currentColor"/>
                  </svg>
              </a>
              <button
                className="tailor-button"
                onClick={handleTailorButtonClick}
              >
                Tailor My Resume
              </button>
            </div>
          </div>
          {selectedSection === "job-details" && <hr className="separator-line" />}
        </div>
        <div className="modal-body">
          {renderContent()}
        </div>
      </div>
    </div>,
    document.getElementById('job-details-modal-root')!
  );
};

export default JobDetailsModal;